import { Box, Divider, Paper, Stack, Typography } from '@mui/material'
import { FixedFab, PageLayout } from '@goschool/components'
import { useParams } from 'react-router-dom'
import { NotFound, typeConverter, useFirestore, useFirestoreSnapshot } from '@goschool/react-firebase'
import { useCallback, useMemo, useState } from 'react'
import { PdfViewer } from './components/PdfViewer'
import { DocumentEditDialog } from './components/DocumentEditDialog'
import type { QueryDocumentSnapshot } from 'firebase/firestore'
import { doc } from 'firebase/firestore'
import type { Course, CourseDocument, GoSchoolUserRole } from '@goschool/model'
import { courseDocumentConverter, useCourse } from '@goschool/model'
import EditIcon from '@mui/icons-material/Edit'
import { useUserContext } from '@goschool/auth'
import { DocumentState, useDocumentState } from './components/CourseDocumentList'
import { GoSchool } from '@goschool/routing'
import { useCourseDocumentsPageBreadcrumb } from '../course'
import { useTranslation } from 'react-i18next'
import { MessageMarkdownComponent } from '@goschool/shared/chat'

export function useDocumentBreadcrumbs(
  documentSnapshot: QueryDocumentSnapshot<CourseDocument>,
  courseSnapshot: QueryDocumentSnapshot<Course>
) {
  const documentsBreadcrumbs = useCourseDocumentsPageBreadcrumb(courseSnapshot)

  if (!documentSnapshot.exists()) {
    return []
  }
  return [
    ...documentsBreadcrumbs,
    {
      title: documentSnapshot.data().title,
      href: GoSchool.document(documentSnapshot.ref),
    },
  ]
}

export function CourseDocumentPage() {
  const { organizationId, courseId, documentId } = useParams<{
    organizationId: string
    courseId: string
    documentId: string
  }>()

  const firestore = useFirestore()
  const documentRef = useMemo(() => {
    if (organizationId != null && courseId != null && documentId != null) {
      return doc(
        firestore,
        'organizations',
        organizationId,
        'courses',
        courseId,
        'documents',
        documentId
      ).withConverter(courseDocumentConverter)
    }
    return null
  }, [firestore, organizationId, courseId, documentId])
  const documentSnapshot = useFirestoreSnapshot(documentRef, { watch: true })
  const pdfDocumentSnapshot = useFirestoreSnapshot(documentRef, {
    watch: false,
  })
  const courseSnapshot = useCourse(organizationId, courseId)

  if (
    documentSnapshot === undefined ||
    pdfDocumentSnapshot === undefined ||
    courseSnapshot === undefined
  ) {
    return null
  }

  if (
    !documentSnapshot?.exists() ||
    !pdfDocumentSnapshot?.exists() ||
    !courseSnapshot?.exists()
  ) {
    throw new NotFound()
  }

  return (
    <CourseDocumentPageContent
      courseSnapshot={courseSnapshot}
      documentSnapshot={documentSnapshot}
      pdfDocumentSnapshot={pdfDocumentSnapshot}
    />
  )
}

function CourseDocumentPageContent({
  documentSnapshot,
  courseSnapshot,
  pdfDocumentSnapshot,
}: {
  documentSnapshot: QueryDocumentSnapshot<CourseDocument>
  courseSnapshot: QueryDocumentSnapshot<Course>
  pdfDocumentSnapshot: QueryDocumentSnapshot<CourseDocument>
}) {
  const { t } = useTranslation()
  const document = useMemo(() => documentSnapshot.data(), [documentSnapshot])
  const pdfStorageRef = useMemo(
    () =>
      pdfDocumentSnapshot.data().storage_ref ??
      document.storage_ref ??
      undefined,
    [pdfDocumentSnapshot, document]
  )
  const metadata = document.metadata ?? undefined
  const title = useMemo(() => {
    const mdTitle = metadata?.title ?? undefined
    if (mdTitle && document.title.toLowerCase().endsWith('.pdf')) {
      return mdTitle
    }
    return document.title || mdTitle || t('document:page.untitled')
  }, [document, metadata, t])
  const subtitle = document.subtitle ?? metadata?.subtitle
  const authors = document.authors ?? metadata?.authors ?? metadata?.speakers
  const abstract = document.abstract ?? metadata?.abstract

  const { roles } = useUserContext()

  const state = useDocumentState(document)
  const [editing, setEditing] = useState(false)
  const showEditor = useCallback(() => setEditing(true), [])
  const hideEditor = useCallback(() => setEditing(false), [])

  return (
    <PageLayout
      fullScreen={false}
      breadcrumbs={useDocumentBreadcrumbs(documentSnapshot, courseSnapshot)}
    >
      <Stack>
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="subtitle1">{subtitle}</Typography>
        ) : null}
        <DocumentState state={state} />
        {authors?.length ? (
          <Typography variant="body1">{authors.join(', ')}</Typography>
        ) : null}
        {abstract ? (
          <Typography variant="caption">{abstract}</Typography>
        ) : null}
      </Stack>
      <DocumentView
        document={document}
        roles={roles}
        documentSnapshot={documentSnapshot}
        pdfStorageRef={pdfStorageRef}
      />
      <DocumentEditDialog
        documentSnapshot={documentSnapshot}
        open={editing}
        onClose={hideEditor}
        close={hideEditor}
      />
      <FixedFab
        onClick={showEditor}
        horizontal="center"
        vertical="bottom"
        color="primary"
      >
        <EditIcon />
      </FixedFab>
    </PageLayout>
  )
}

interface DocumentPageContent {
  markdown_content?: string | null
  image_descriptions?: string[] | null
}

interface DocumentPageState {
  page_index?: number
  content?: DocumentPageContent | null
  refined_content?: DocumentPageContent | null
  content_extraction?: { status?: string | null } | null
  visual_refinement?: { status?: string | null } | null
  index?: {
    status?: string | null
    plain_text_status?: string | null
    refined_status?: string | null
  } | null
  state?: {
    ready?: boolean | null
    quick?: {
      status?: string | null
      page_text?: string | null
    } | null
    visual?: { status?: string | null } | null
    finalizing?: { status?: string | null } | null
  } | null
}

function DocumentView({
  document,
  roles,
  documentSnapshot,
  pdfStorageRef,
}: {
  document: CourseDocument
  roles: GoSchoolUserRole[]
  documentSnapshot: QueryDocumentSnapshot<CourseDocument>
  pdfStorageRef: string | undefined
}) {
  const firestore = useFirestore()
  const allowed =
    roles.includes('instructor') ||
    roles.includes('admin') ||
    document.availability === 'public'
  const isAdmin = roles.includes('admin')
  const isPdf = document.content_type === 'application/pdf'
  const [pageNumber, setPageNumber] = useState(1)
  const pageDocId = useMemo(
    () => String(Math.max(1, pageNumber)).padStart(4, '0'),
    [pageNumber]
  )
  const documentPath = documentSnapshot.ref.path
  const pageRef = useMemo(
    () =>
      isAdmin && isPdf
        ? doc(firestore, documentPath, 'pages', pageDocId).withConverter(
          typeConverter<DocumentPageState>()
        )
        : null,
    [firestore, documentPath, pageDocId, isAdmin, isPdf]
  )
  const pageSnapshot = useFirestoreSnapshot(pageRef)
  const pageState = pageSnapshot?.exists() ? pageSnapshot.data() : undefined
  const onPageChange = useCallback(
    (nextPageNumber: number) => setPageNumber(nextPageNumber),
    []
  )

  if (!allowed) {
    return null
  }

  if (isPdf) {
    if (!isAdmin) {
      return <Box flex={1} minWidth={0}>
        <PdfViewer storageRef={pdfStorageRef} onPageChange={onPageChange} />
      </Box>
    }

    return (
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems="stretch"
      >
        <Box flex={1} minWidth={0}>
          <PdfViewer storageRef={pdfStorageRef} onPageChange={onPageChange} />
        </Box>
        <ProcessedPageContent pageNumber={pageNumber} pageState={pageState} />
      </Stack>
    )
  }

  return null
}

export function ProcessedPageContent({
  pageNumber,
  pageState,
}: {
  pageNumber: number
  pageState: DocumentPageState | undefined
}) {
  const { t } = useTranslation()

  const [
    content,
    markdownContent,
    imageDescriptions,
    extractionStatus,
    refinementStatus,
    indexingStatus,
    hasContent,
  ] = useMemo(() => {
    const quickPageText = (pageState?.state?.quick?.page_text ?? '').trim()
    const quickContent: DocumentPageContent | null = quickPageText
      ? { markdown_content: quickPageText, image_descriptions: [] }
      : null
    const content =
      pageState?.refined_content ?? pageState?.content ?? quickContent
    const markdownContent = (content?.markdown_content ?? '').trim()
    const imageDescriptions = (content?.image_descriptions ?? []).filter(
      Boolean
    )
    const extractionStatus =
      pageState?.content_extraction?.status ?? pageState?.state?.quick?.status
    const refinementStatus =
      pageState?.visual_refinement?.status ?? pageState?.state?.visual?.status
    const indexingStatus =
      pageState?.index?.status ??
      pageState?.index?.refined_status ??
      pageState?.index?.plain_text_status ??
      pageState?.state?.finalizing?.status
    const hasContent =
      markdownContent.length > 0 || imageDescriptions.length > 0

    return [
      content,
      markdownContent,
      imageDescriptions,
      extractionStatus,
      refinementStatus,
      indexingStatus,
      hasContent,
    ]
  }, [pageState])

  const localizedStatus = useCallback(
    (status: string) =>
      t(`document:processedPage.status.${status}`, { defaultValue: status }),
    [t]
  )

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: { lg: 380 },
        maxWidth: { lg: 460 },
        maxHeight: 800,
        overflowY: 'auto',
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="h6">
          {t('document:processedPage.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('document:processedPage.page', { page: pageNumber })}
        </Typography>

        {extractionStatus ? (
          <Typography variant="caption" color="text.secondary">
            {t('document:processedPage.extractionStatus', {
              status: localizedStatus(extractionStatus),
            })}
          </Typography>
        ) : null}
        {indexingStatus ? (
          <Typography variant="caption" color="text.secondary">
            {t('document:processedPage.indexStatus', {
              status: localizedStatus(indexingStatus),
            })}
          </Typography>
        ) : null}
        {refinementStatus ? (
          <Typography variant="caption" color="text.secondary">
            {t('document:processedPage.refinementStatus', {
              status: localizedStatus(refinementStatus),
            })}
          </Typography>
        ) : null}

        {!hasContent ? (
          <Typography variant="body2" color="text.secondary">
            {t('document:processedPage.empty')}
          </Typography>
        ) : (
          <>
            {markdownContent ? (
              <>
                <Typography variant="subtitle2">
                  {t('document:processedPage.markdown')}
                </Typography>
                <MessageMarkdownComponent>
                  {markdownContent}
                </MessageMarkdownComponent>
              </>
            ) : null}

            <Divider />

            <Typography variant="subtitle2">
              {t('document:processedPage.images')}
            </Typography>
            {imageDescriptions.length > 0 ? (
              <Stack spacing={1}>
                {imageDescriptions.map((description, index) => (
                  <MessageMarkdownComponent
                    key={`${pageNumber}-image-description-${index}`}
                  >
                    {description}
                  </MessageMarkdownComponent>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('document:processedPage.noImages')}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Paper>
  )
}
